import { useEffect, useMemo, useRef, useState } from "react";
import {
  Search, Plus, X, CalendarPlus, MessageSquare, Mail, Phone, User, Banknote,
  Loader2, BadgeCheck, Sparkles, UserPlus, Trash2, Eye, Landmark, StickyNote, Send,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/format";
import { DateField } from "@/components/DatePickers";
import { toast } from "sonner";

const PAYMENT_LABELS = { cash: "Cash", zelle: "Zelle", cashapp: "Cash App", venmo: "Venmo" };
const TAG_STYLES = {
  account: "bg-blue-50 text-blue-600",
  guest: "bg-emerald-50 text-emerald-600",
  lead: "bg-amber-50 text-amber-700",
  customer: "bg-indigo-50 text-indigo-600",
};
const slug = (s) => (s || "").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

export function ContactsCRM({ cars, onMutate }) {
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [resModal, setResModal] = useState(null);
  const [notify, setNotify] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = (query = "") => {
    setLoading(true);
    api.get(`/admin/crm/contacts${query ? `?q=${encodeURIComponent(query)}` : ""}`)
      .then((r) => setContacts(r.data)).catch(() => setContacts([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { const t = setTimeout(() => load(q), 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const onReservationDone = () => { setResModal(null); load(q); onMutate?.(); };

  const deleteCustomer = async (cid) => {
    if (!window.confirm("Remove this customer from your CRM?")) return;
    try {
      await api.delete(`/admin/crm/customers/${cid}`);
      toast.success("Customer removed");
      load(q);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="contacts-crm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="crm-search"
            placeholder="Search by name, email, phone, stock # or make/model…" className="belo-input pl-9 w-80" />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setAddOpen(true)} data-testid="add-customer-btn"
            className="flex items-center gap-2 bg-white border-2 border-slate-200 text-slate-700 px-5 py-2.5 rounded-full text-sm font-semibold hover:border-slate-300 transition-colors">
            <UserPlus size={16} /> Add Customer
          </button>
          <button onClick={() => setResModal({})} data-testid="new-reservation-btn"
            className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-blue-700 transition-colors">
            <CalendarPlus size={16} /> New Reservation
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-card overflow-hidden">
        {loading ? (
          <div className="p-10 flex items-center gap-2 text-slate-400 text-sm"><Loader2 size={16} className="animate-spin" /> Loading contacts…</div>
        ) : contacts.length === 0 ? (
          <p className="text-slate-400 p-10 text-sm">{q ? "No contacts match your search." : "No contacts yet. Customers, guests, and contact-form leads will appear here."}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left label-caps text-slate-400 border-b border-slate-100 bg-slate-50">
                <th className="py-4 px-5">Contact</th><th className="py-4 px-3">Type</th><th className="py-4 px-3">Bookings</th><th className="py-4 px-3">Total spent</th><th className="py-4 px-3">Last activity</th><th className="py-4 px-3 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c.email} data-testid={`contact-row-${slug(c.email)}`} className="border-b border-slate-50 hover:bg-slate-50/60 align-top">
                    <td className="py-3.5 px-5">
                      <button onClick={() => setDetail(c.email)} data-testid={`contact-open-${slug(c.email)}`} className="text-slate-900 font-medium hover:text-blue-600 transition-colors text-left">{c.name || "—"}</button>
                      <div className="text-slate-400 text-xs flex items-center gap-1.5"><Mail size={11} />{c.email}</div>
                      {c.phone && <div className="text-slate-400 text-xs flex items-center gap-1.5 mt-0.5"><Phone size={11} />{c.phone}</div>}
                      {c.lead_message && <div className="mt-1.5 text-xs text-amber-700 bg-amber-50 rounded-lg px-2 py-1 max-w-xs">“{c.lead_message}”</div>}
                      {c.notes && <div className="mt-1.5 text-xs text-slate-500 bg-slate-50 rounded-lg px-2 py-1 max-w-xs">{c.notes}</div>}
                    </td>
                    <td className="py-3.5 px-3">
                      <div className="flex flex-wrap gap-1">
                        {(c.tags || []).map((t) => (
                          <span key={t} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ${TAG_STYLES[t] || "bg-slate-100 text-slate-500"}`}>{t}</span>
                        ))}
                      </div>
                    </td>
                    <td className="py-3.5 px-3 text-slate-700">{c.bookings || 0}{c.vehicles?.length > 0 && <div className="text-slate-400 text-xs max-w-[200px] truncate" title={c.vehicles.join(", ")}>{c.vehicles.join(", ")}</div>}</td>
                    <td className="py-3.5 px-3 font-medium text-slate-900">{money(c.total_spent || 0)}</td>
                    <td className="py-3.5 px-3 text-slate-500 text-xs">{c.last_activity ? new Date(c.last_activity).toLocaleDateString() : "—"}</td>
                    <td className="py-3.5 px-3">
                      <div className="flex items-center gap-2 justify-end">
                        <button onClick={() => setDetail(c.email)} data-testid={`contact-view-${slug(c.email)}`} title="View profile" className="text-slate-400 hover:text-blue-600 transition-colors"><Eye size={16} /></button>
                        <button onClick={() => setResModal({ customer_name: c.name, customer_email: c.email, customer_phone: c.phone })}
                          data-testid={`contact-book-${slug(c.email)}`} title="New reservation" className="text-slate-400 hover:text-blue-600 transition-colors"><CalendarPlus size={16} /></button>
                        <button onClick={() => setNotify(c)} data-testid={`contact-message-${slug(c.email)}`} title="Send message" className="text-slate-400 hover:text-blue-600 transition-colors"><MessageSquare size={16} /></button>
                        {c.deletable && c.crm_id && (
                          <button onClick={() => deleteCustomer(c.crm_id)} data-testid={`contact-delete-${slug(c.email)}`} title="Delete customer" className="text-slate-400 hover:text-rose-600 transition-colors"><Trash2 size={16} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {resModal && <NewReservationModal prefill={resModal} cars={cars} onClose={() => setResModal(null)} onDone={onReservationDone} />}
      {notify && <NotifyModal contact={notify} onClose={() => setNotify(null)} />}
      {addOpen && <AddCustomerModal onClose={() => setAddOpen(false)} onDone={() => { setAddOpen(false); load(q); }} />}
      {detail && <CustomerDrawer email={detail} onClose={() => setDetail(null)} onMessage={(c) => { setDetail(null); setNotify(c); }} onBook={(c) => { setDetail(null); setResModal({ customer_name: c.name, customer_email: c.email, customer_phone: c.phone }); }} />}
    </div>
  );
}

function AddCustomerModal({ onClose, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required."); return; }
    setSaving(true);
    try {
      await api.post("/admin/crm/customers", form);
      toast.success("Customer added");
      onDone();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="add-customer-modal">
      <div className="bg-white rounded-2xl shadow-card-hover w-full max-w-md my-8">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="font-heading text-xl font-semibold">Add Customer</h2>
          <button onClick={onClose} data-testid="add-customer-close" className="text-slate-400 hover:text-slate-900"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <label className="block"><span className="label-caps text-slate-400 block mb-2">Full name</span>
            <input value={form.name} onChange={set("name")} data-testid="add-customer-name" required className="belo-input" placeholder="Jane Driver" /></label>
          <label className="block"><span className="label-caps text-slate-400 block mb-2">Email</span>
            <input type="email" value={form.email} onChange={set("email")} data-testid="add-customer-email" required className="belo-input" placeholder="jane@example.com" /></label>
          <label className="block"><span className="label-caps text-slate-400 block mb-2">Phone</span>
            <input value={form.phone} onChange={set("phone")} data-testid="add-customer-phone" className="belo-input" placeholder="(555) 555-5555" /></label>
          <label className="block"><span className="label-caps text-slate-400 block mb-2">Notes</span>
            <textarea value={form.notes} onChange={set("notes")} data-testid="add-customer-notes" rows={2} className="belo-input resize-none" placeholder="Optional internal notes" /></label>
          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="border-2 border-slate-200 px-6 py-2.5 rounded-full font-semibold text-slate-700 hover:bg-slate-50 transition-colors">Cancel</button>
            <button type="submit" disabled={saving} data-testid="add-customer-submit" className="bg-blue-600 text-white px-7 py-2.5 rounded-full font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2">
              {saving ? <><Loader2 size={16} className="animate-spin" /> Adding…</> : "Add Customer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function NewReservationModal({ prefill, cars, onClose, onDone }) {
  const listed = (cars || []).filter((c) => c.review_status !== "rejected");
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({
    car_id: listed[0]?.id || "",
    customer_name: prefill.customer_name || "", customer_email: prefill.customer_email || "",
    customer_phone: prefill.customer_phone || "", date_of_birth: "",
    pickup_date: "", return_date: "", pickup_location: "",
    refundable: true, airport_dropoff: false, unlimited_miles: false, discount_code: "",
    payment: "", notes: "",
  });
  const [quote, setQuote] = useState(null);
  const [quoteErr, setQuoteErr] = useState("");
  const [quoting, setQuoting] = useState(false);
  const [saving, setSaving] = useState(false);
  const debounce = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setVal = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/config").then((r) => {
      setConfig(r.data);
      setForm((f) => ({ ...f, pickup_location: f.pickup_location || r.data.pickup_locations?.[0] || "" }));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.car_id || !form.pickup_date || !form.return_date) { setQuote(null); setQuoteErr(""); return; }
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      setQuoting(true);
      api.post("/bookings/quote", {
        car_id: form.car_id, date_of_birth: form.date_of_birth, pickup_date: form.pickup_date,
        return_date: form.return_date, pickup_location: form.pickup_location, refundable: form.refundable,
        discount_code: form.discount_code, airport_dropoff: form.airport_dropoff, unlimited_miles: form.unlimited_miles,
      }).then((r) => { setQuote(r.data); setQuoteErr(""); })
        .catch((e) => { setQuote(null); setQuoteErr(formatApiError(e.response?.data?.detail)); })
        .finally(() => setQuoting(false));
    }, 350);
    return () => clearTimeout(debounce.current);
  }, [form.car_id, form.pickup_date, form.return_date, form.pickup_location, form.refundable, form.discount_code, form.airport_dropoff, form.unlimited_miles, form.date_of_birth]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim() || !form.customer_email.trim()) { toast.error("Customer name and email are required."); return; }
    if (!quote) { toast.error("Pick valid dates to price the reservation."); return; }
    setSaving(true);
    try {
      await api.post("/admin/crm/bookings", {
        car_id: form.car_id, customer_name: form.customer_name, customer_email: form.customer_email,
        customer_phone: form.customer_phone, date_of_birth: form.date_of_birth || "",
        pickup_date: form.pickup_date, return_date: form.return_date, pickup_location: form.pickup_location,
        refundable: form.refundable, discount_code: form.discount_code, notes: form.notes,
        airport_dropoff: form.airport_dropoff, unlimited_miles: form.unlimited_miles,
        payment_method: form.payment || null, mark_paid: !!form.payment,
      });
      toast.success(form.payment ? `Reservation booked & marked paid (${PAYMENT_LABELS[form.payment]})` : "Reservation booked");
      onDone();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const today = startOfToday();
  const pickupObj = form.pickup_date ? new Date(form.pickup_date + "T00:00:00") : null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="new-reservation-modal">
      <div className="bg-white rounded-2xl shadow-card-hover w-full max-w-3xl my-8">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div>
            <h2 className="font-heading text-xl font-semibold">New Reservation</h2>
            <p className="text-slate-400 text-xs mt-0.5">Book on a customer's behalf. The e-signed agreement is handled in person.</p>
          </div>
          <button onClick={onClose} data-testid="res-modal-close" className="text-slate-400 hover:text-slate-900"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-5 gap-0 max-h-[78vh] overflow-y-auto">
          <div className="lg:col-span-3 p-6 space-y-4 border-r border-slate-100">
            <p className="label-caps text-slate-400 flex items-center gap-1.5"><User size={13} /> Customer</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block sm:col-span-2"><span className="label-caps text-slate-400 block mb-2">Full name</span>
                <input value={form.customer_name} onChange={set("customer_name")} data-testid="res-customer-name" required className="belo-input" placeholder="Jane Driver" /></label>
              <label className="block"><span className="label-caps text-slate-400 block mb-2">Email</span>
                <input type="email" value={form.customer_email} onChange={set("customer_email")} data-testid="res-customer-email" required className="belo-input" placeholder="jane@example.com" /></label>
              <label className="block"><span className="label-caps text-slate-400 block mb-2">Phone</span>
                <input value={form.customer_phone} onChange={set("customer_phone")} data-testid="res-customer-phone" className="belo-input" placeholder="(555) 555-5555" /></label>
            </div>

            <p className="label-caps text-slate-400 flex items-center gap-1.5 pt-2"><CalendarPlus size={13} /> Trip</p>
            <label className="block"><span className="label-caps text-slate-400 block mb-2">Vehicle</span>
              <select value={form.car_id} onChange={set("car_id")} data-testid="res-car" required className="belo-input">
                {listed.map((c) => <option key={c.id} value={c.id}>{c.name}{c.host_name ? ` · ${c.host_name}` : ""}</option>)}
              </select></label>
            <div className="grid grid-cols-2 gap-4">
              <div><span className="label-caps text-slate-400 block mb-2">Pickup</span>
                <DateField value={form.pickup_date} onChange={(v) => setForm((f) => ({ ...f, pickup_date: v, return_date: f.return_date && f.return_date <= v ? "" : f.return_date }))} fromDate={today} toDate={addDays(today, 365)} placeholder="Pickup date" testid="res-pickup-date" /></div>
              <div><span className="label-caps text-slate-400 block mb-2">Return</span>
                <DateField value={form.return_date} onChange={(v) => setVal("return_date", v)} fromDate={pickupObj ? addDays(pickupObj, 1) : addDays(today, 1)} toDate={addDays(pickupObj || today, 365)} placeholder="Return date" testid="res-return-date" /></div>
            </div>
            <label className="block"><span className="label-caps text-slate-400 block mb-2">Pickup location</span>
              <select value={form.pickup_location} onChange={set("pickup_location")} data-testid="res-pickup-location" className="belo-input">
                {(config?.pickup_locations || []).map((loc) => <option key={loc} value={loc}>{loc}</option>)}
              </select></label>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer"><input type="checkbox" checked={form.airport_dropoff} onChange={(e) => setVal("airport_dropoff", e.target.checked)} data-testid="res-airport" className="w-4 h-4 accent-blue-600" /> Logan Airport drop-off</label>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer"><input type="checkbox" checked={form.unlimited_miles} onChange={(e) => setVal("unlimited_miles", e.target.checked)} data-testid="res-unlimited" className="w-4 h-4 accent-blue-600" /> Unlimited miles</label>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer"><input type="checkbox" checked={!form.refundable} onChange={(e) => setVal("refundable", !e.target.checked)} data-testid="res-nonref" className="w-4 h-4 accent-blue-600" /> Non-refundable (−10%)</label>
            </div>
            <label className="block"><span className="label-caps text-slate-400 block mb-2">Discount code (optional)</span>
              <input value={form.discount_code} onChange={set("discount_code")} data-testid="res-discount" className="belo-input" placeholder="SAVE25" /></label>
            <label className="block"><span className="label-caps text-slate-400 block mb-2">Notes (internal)</span>
              <textarea value={form.notes} onChange={set("notes")} data-testid="res-notes" rows={2} className="belo-input resize-none" /></label>
          </div>

          <div className="lg:col-span-2 p-6 bg-slate-50 space-y-4">
            <p className="label-caps text-slate-400 flex items-center gap-1.5"><Sparkles size={13} /> Price</p>
            <div className="bg-white rounded-xl p-4 shadow-card text-sm space-y-2 min-h-[120px]" data-testid="res-quote">
              {quoting ? <p className="text-slate-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Pricing…</p>
                : quoteErr ? <p className="text-red-600 text-xs">{quoteErr}</p>
                : !quote ? <p className="text-slate-400 text-xs">Pick pickup and return dates to see the price.</p>
                : (
                  <>
                    <Row k={`Rental (${quote.days}d)`} v={money(quote.rental)} />
                    {quote.unlimited_miles_fee > 0 && <Row k="Unlimited miles" v={money(quote.unlimited_miles_fee)} />}
                    {quote.airport_fee > 0 && <Row k="Airport fee" v={money(quote.airport_fee)} />}
                    {quote.young_driver_fee > 0 && <Row k="Young driver fee" v={money(quote.young_driver_fee)} />}
                    {quote.discount_amount > 0 && <Row k={`Discount${quote.discount_code ? ` (${quote.discount_code})` : ""}`} v={`−${money(quote.discount_amount)}`} />}
                    <Row k="Tax (6.25%)" v={money(quote.tax)} />
                    <Row k="Refundable deposit" v={money(quote.deposit)} />
                    <div className="border-t border-slate-100 pt-2 mt-1 flex justify-between font-semibold text-slate-900"><span>Total due</span><span data-testid="res-quote-total">{money(quote.amount_due)}</span></div>
                    {quote.discount_error && <p className="text-amber-600 text-xs">{quote.discount_error}</p>}
                  </>
                )}
            </div>

            <p className="label-caps text-slate-400 flex items-center gap-1.5"><Banknote size={13} /> Payment</p>
            <label className="block"><span className="text-xs text-slate-500 block mb-2">Record an offline payment now, or leave unpaid to collect later.</span>
              <select value={form.payment} onChange={set("payment")} data-testid="res-payment-method" className="belo-input">
                <option value="">Unpaid — collect later</option>
                <option value="cash">Paid · Cash</option>
                <option value="zelle">Paid · Zelle</option>
                <option value="cashapp">Paid · Cash App</option>
                <option value="venmo">Paid · Venmo</option>
              </select></label>
            {form.payment && <p className="text-emerald-600 text-xs flex items-center gap-1.5"><BadgeCheck size={14} /> Will be marked Confirmed & Paid ({PAYMENT_LABELS[form.payment]}).</p>}

            <div className="flex flex-col gap-2 pt-2">
              <button type="submit" disabled={saving || !quote} data-testid="res-submit"
                className="bg-blue-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                {saving ? <><Loader2 size={16} className="animate-spin" /> Booking…</> : "Create Reservation"}
              </button>
              <button type="button" onClick={onClose} className="border-2 border-slate-200 px-6 py-2.5 rounded-full font-semibold text-slate-700 hover:bg-white transition-colors">Cancel</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between text-slate-600"><span>{k}</span><span className="text-slate-900">{v}</span></div>;
}

function NotifyModal({ contact, onClose }) {
  const [channel, setChannel] = useState(contact.email ? "email" : "sms");
  const [subject, setSubject] = useState("A message from Belo Rentals");
  const [message, setMessage] = useState(`Hi ${contact.name || "there"},\n\n`);
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!message.trim()) { toast.error("Message cannot be empty."); return; }
    setSending(true);
    try {
      const r = await api.post("/admin/crm/notify", { channel, email: contact.email, phone: contact.phone, subject, message });
      toast[r.data.sent ? "success" : "info"](r.data.message);
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="notify-modal">
      <div className="bg-white rounded-2xl shadow-card-hover w-full max-w-lg my-8">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div>
            <h2 className="font-heading text-xl font-semibold">Message {contact.name || contact.email}</h2>
            <p className="text-slate-400 text-xs mt-0.5">{channel === "email" ? contact.email : (contact.phone || "No phone on file")}</p>
          </div>
          <button onClick={onClose} data-testid="notify-modal-close" className="text-slate-400 hover:text-slate-900"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="inline-flex bg-slate-100 rounded-full p-1">
            <button onClick={() => setChannel("email")} disabled={!contact.email} data-testid="notify-channel-email"
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors disabled:opacity-40 ${channel === "email" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}><Mail size={14} /> Email</button>
            <button onClick={() => setChannel("sms")} disabled={!contact.phone} data-testid="notify-channel-sms"
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors disabled:opacity-40 ${channel === "sms" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}><Phone size={14} /> Text</button>
          </div>
          {channel === "email" && (
            <label className="block"><span className="label-caps text-slate-400 block mb-2">Subject</span>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="notify-subject" className="belo-input" /></label>
          )}
          <label className="block"><span className="label-caps text-slate-400 block mb-2">Message</span>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} data-testid="notify-message" rows={6} className="belo-input resize-none" /></label>
          <div className="flex justify-end gap-3">
            <button onClick={onClose} className="border-2 border-slate-200 px-6 py-2.5 rounded-full font-semibold text-slate-700 hover:bg-slate-50 transition-colors">Cancel</button>
            <button onClick={send} disabled={sending} data-testid="notify-send"
              className="bg-blue-600 text-white px-7 py-2.5 rounded-full font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2">
              {sending ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


const STATUS_PILL = {
  paid: "bg-emerald-50 text-emerald-600", unpaid: "bg-amber-50 text-amber-600",
  cancelled: "bg-rose-50 text-rose-500", confirmed: "bg-blue-50 text-blue-600",
};

function Stat({ label, value }) {
  return <div className="bg-slate-50 rounded-xl p-3"><p className="text-lg font-bold font-heading text-slate-900">{value}</p><p className="text-[11px] text-slate-500">{label}</p></div>;
}

function CustomerDrawer({ email, onClose, onMessage, onBook }) {
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/admin/crm/customer`, { params: { email } }).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [email]);

  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try { await api.post("/admin/crm/customer/notes", { email, note }); setNote(""); load(); toast.success("Note added."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const delNote = async (id) => { try { await api.delete(`/admin/crm/customer/notes/${id}`); load(); } catch { /* ignore */ } };

  return (
    <div className="fixed inset-0 z-[65] bg-black/40 flex justify-end" onClick={onClose} data-testid="customer-drawer">
      <div className="bg-slate-50 w-full max-w-xl h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {!data ? (
          <div className="p-10 flex items-center gap-2 text-slate-400 text-sm"><Loader2 size={16} className="animate-spin" /> Loading profile…</div>
        ) : (
          <div>
            <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-start justify-between z-10">
              <div>
                <h3 className="font-heading text-xl font-semibold text-slate-900">{data.name || "Customer"}</h3>
                <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-0.5"><Mail size={12} />{data.email}</p>
                {data.phone && <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-0.5"><Phone size={12} />{data.phone}</p>}
                <div className="flex gap-1.5 mt-2">
                  {data.has_account && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">Account</span>}
                  {data.is_host && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">Host</span>}
                  {data.bank_on_file && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600">ACH on file</span>}
                </div>
              </div>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-4 gap-3" data-testid="crm-revenue">
                <Stat label="Revenue" value={money(data.revenue)} />
                <Stat label="Paid trips" value={data.paid_trips} />
                <Stat label="Bookings" value={data.total_bookings} />
                <Stat label="Cancelled" value={data.cancelled} />
              </div>

              <div className="flex gap-2">
                <button onClick={() => onBook(data)} data-testid="drawer-book" className="flex-1 flex items-center justify-center gap-2 rounded-full bg-blue-600 text-white py-2.5 text-sm font-semibold hover:bg-blue-700"><CalendarPlus size={15} /> New reservation</button>
                <button onClick={() => onMessage(data)} data-testid="drawer-message" className="flex-1 flex items-center justify-center gap-2 rounded-full border-2 border-slate-200 text-slate-700 py-2.5 text-sm font-semibold hover:border-slate-300"><MessageSquare size={15} /> Message</button>
              </div>

              {/* Billing on file */}
              <div className="bg-white rounded-2xl border border-slate-100 p-5">
                <h4 className="font-heading font-semibold text-slate-900 flex items-center gap-2 mb-3"><Landmark size={16} className="text-blue-600" /> Billing on file</h4>
                <div className="text-sm text-slate-600 space-y-1">
                  <p>Payment methods used: <b className="text-slate-900">{data.payment_methods.length ? data.payment_methods.map((m) => PAYMENT_LABELS[m] || m).join(", ") : "—"}</b></p>
                  <p>ACH bank account: <b className="text-slate-900">{data.bank_on_file ? "On file" : "Not provided"}</b></p>
                </div>
              </div>

              {/* Rental history */}
              <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="crm-rental-history">
                <h4 className="font-heading font-semibold text-slate-900 mb-3">Rental history</h4>
                {data.rental_history.length === 0 ? <p className="text-sm text-slate-400">No reservations yet.</p> : (
                  <div className="space-y-2">
                    {data.rental_history.map((b) => (
                      <div key={b.id} className="flex items-center justify-between text-sm border-b border-slate-50 pb-2 last:border-0">
                        <div>
                          <p className="font-medium text-slate-900">{b.car_name || "Reservation"}</p>
                          <p className="text-xs text-slate-400">{b.pickup_date} → {b.return_date} · {b.days}d{b.payment_method ? ` · ${PAYMENT_LABELS[b.payment_method] || b.payment_method}` : ""}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-slate-900">{money(b.amount_due)}</p>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_PILL[b.status] || STATUS_PILL[b.payment_status] || "bg-slate-100 text-slate-500"}`}>{b.status === "cancelled" ? "cancelled" : b.payment_status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Communication history */}
              <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="crm-comms">
                <h4 className="font-heading font-semibold text-slate-900 mb-3">Communication history</h4>
                {data.messages.length === 0 ? <p className="text-sm text-slate-400">No messages exchanged yet.</p> : (
                  <div className="space-y-2 max-h-52 overflow-y-auto">
                    {data.messages.map((m) => (
                      <div key={m.id} className="text-sm">
                        <span className={`font-semibold ${m.sender_role === "admin" ? "text-blue-600" : "text-slate-700"}`}>{m.sender_role === "admin" ? "You" : (m.sender_name || "Guest")}:</span>{" "}
                        <span className="text-slate-600">{m.body}</span>
                        <span className="text-[10px] text-slate-300 ml-1">{new Date(m.created_at).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Notes */}
              <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="crm-notes">
                <h4 className="font-heading font-semibold text-slate-900 flex items-center gap-2 mb-3"><StickyNote size={16} className="text-amber-500" /> Internal notes</h4>
                <div className="flex gap-2 mb-3">
                  <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} data-testid="crm-note-input" placeholder="Add a private note…" className="belo-input flex-1" />
                  <button onClick={addNote} disabled={saving} data-testid="crm-note-add" className="grid place-items-center w-11 rounded-full bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"><Send size={15} /></button>
                </div>
                {data.crm_notes_legacy && <div className="text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2 mb-2">{data.crm_notes_legacy}</div>}
                <div className="space-y-2">
                  {data.notes.map((n) => (
                    <div key={n.id} className="flex items-start justify-between gap-2 text-sm bg-amber-50/50 rounded-lg px-3 py-2">
                      <div><p className="text-slate-700">{n.body}</p><p className="text-[10px] text-slate-400 mt-0.5">{n.author} · {new Date(n.created_at).toLocaleString()}</p></div>
                      <button onClick={() => delNote(n.id)} className="text-slate-300 hover:text-rose-500 shrink-0"><Trash2 size={13} /></button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
